import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { MultiSelect } from '../../../components/ui/MultiSelect.jsx';
import { TaxonomyPicker } from './TaxonomyPicker.jsx';
import { ProductLinkPicker } from './ProductLinkPicker.jsx';
import { ragApi } from '../../../api/rag.js';
import { storesApi } from '../../../api/stores.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';
import { SOURCE_TYPES } from './ragConstants.js';

const EMPTY = { title: '', description: '', sourceType: 'misc', language: 'it', tags: [], storeIds: [], speciesId: '', breedId: '', productId: '' };

// Tags driven by the species/breed picker are not shown in the free TagInput;
// the server re-derives them from the selection on save.
const isTaxonomyTag = (tag) => /^(species|breed):/.test(String(tag));

export function EditDocumentModal({ open, onClose, document }) {
  const queryClient = useQueryClient();

  const storesQuery = useQuery({ queryKey: ['stores', 'all'], enabled: open, queryFn: () => storesApi.list({ limit: 100 }) });
  const storeOpts = useMemo(
    () => ((storesQuery.data && storesQuery.data.items) || []).map((s) => ({ value: s._id, label: s.name, hint: s.code })),
    [storesQuery.data]
  );

  const { register, handleSubmit, control, reset, setError, watch, setValue, formState: { errors } } = useForm({ defaultValues: EMPTY });
  const speciesId = watch('speciesId');
  const breedId = watch('breedId');
  const productId = watch('productId');
  const sourceType = watch('sourceType');
  const isProductDoc = sourceType === 'product';

  useEffect(() => {
    if (!open || !document) return;
    reset({
      title: document.title || '',
      description: document.description || '',
      sourceType: document.sourceType || 'misc',
      language: document.language || 'it',
      tags: (document.tags || []).filter((tag) => !isTaxonomyTag(tag)),
      storeIds: (document.storeIds || []).map((x) => x._id || x),
      speciesId: document.sourceRefType === 'species' ? String(document.sourceRefId || '') : '',
      breedId: document.sourceRefType === 'breed' ? String(document.sourceRefId || '') : '',
      productId: document.sourceRefType === 'Product' ? String(document.sourceRefId || '') : ''
    });
  }, [open, document, reset]);

  const mutation = useMutation({
    mutationFn: (values) => ragApi.update(document._id, {
      title: values.title || document.title,
      description: values.description || '',
      sourceType: values.sourceType,
      language: values.language || 'it',
      tags: (values.tags || []).filter((tag) => !isTaxonomyTag(tag)),
      storeIds: values.storeIds || [],
      // A "scheda prodotto" links to a product; other types link to a
      // species/breed. Send the relevant link so the server (re)applies it.
      ...(values.sourceType === 'product'
        ? { productId: values.productId || '' }
        : { speciesId: values.speciesId || '', breedId: values.breedId || '' })
    }),
    onSuccess: () => {
      toast.success('Documento aggiornato.');
      queryClient.invalidateQueries({ queryKey: ['rag', 'documents'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={document ? `Modifica "${document.title}"` : 'Modifica documento'}
      description="Aggiorna i metadati. Il contenuto indicizzato non cambia: per sostituire il testo usa Reindicizza."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="rag-edit" type="submit" loading={mutation.isPending}>Salva</Button>
        </>
      }
    >
      <form id="rag-edit" className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <Input label="Titolo" error={errors.title?.message} {...register('title')} />
        <Select label="Tipo sorgente" options={SOURCE_TYPES} error={errors.sourceType?.message} {...register('sourceType')} />
        <Input label="Lingua" maxLength={8} {...register('language')} />
        <Controller
          control={control}
          name="tags"
          render={({ field }) => <TagInput label="Tag" value={field.value} onChange={field.onChange} lowercase placeholder="es. cibo, gatti" />}
        />
        <div className="sm:col-span-2">
          <Controller
            control={control}
            name="storeIds"
            render={({ field }) => (
              <MultiSelect
                label="Negozi destinatari"
                hint="Lasciare vuoto per renderlo disponibile a tutti i negozi."
                options={storeOpts}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="sm:col-span-2">
          {isProductDoc ? (
            <ProductLinkPicker
              open={open}
              productId={productId}
              onChange={(id) => setValue('productId', id || '')}
            />
          ) : (
            <TaxonomyPicker
              open={open}
              speciesId={speciesId}
              breedId={breedId}
              onChange={({ speciesId: s, breedId: b }) => {
                setValue('speciesId', s || '');
                setValue('breedId', b || '');
              }}
            />
          )}
        </div>
        <div className="sm:col-span-2">
          <Textarea label="Descrizione" rows={2} error={errors.description?.message} {...register('description')} />
        </div>
      </form>
    </Modal>
  );
}
